import {ApartmentOutlined, ArrowRightOutlined, ArrowUpOutlined, DoubleLeftOutlined, DoubleRightOutlined, ExportOutlined, LeftOutlined, RightOutlined, SettingOutlined} from "@ant-design/icons";
import {Button, Checkbox, Modal, Popover, Select, Spin, Tag} from "antd";
import _ from "lodash";
import {PackageURL} from "packageurl-js";
import React from "react";
import {Link} from "react-router-dom";
import AttributeDefinition from "../../domain/AttributeDefinition";
import SbomComponent from "../../domain/SbomComponent";
import SbomComponentFinding from "../../domain/SbomComponentFinding";
import SbomProject from "../../domain/SbomProject";
import SbomProjectVersion from "../../domain/SbomProjectVersion";
import SingleResult from "../../service/SingleResult";
import {formatDateTime} from "../../utils/FormatUtils";
import VulSourceItemPreview from "../vulsourceitem/VulSourceItemPreview";
import * as sealScoreStyles from "../vulsourceitem/SealScore.module.css";
import {packageUrlToOsiUrl} from "../sbomcomponent/functions";
import FindingAnalysisStatistics, {AnalysisStyle, calculateFindingAnalysisProgress} from "./FindingAnalysisStatistics";
import FindingStatistics from "./FindingStatistics";
import FindingStatisticsImpact from "./FindingStatisticsImpact";
import FindingStatusPanel from "./FindingStatusPanel";
import FocusedFindingSelect, {ComponentFindingsPage} from "./FocusedFindingSelect";
import * as styles from "./SbomProjectVersionDetail.module.css";
import {FocusedFindingAnalysisController} from "./useFocusedFindingAnalysis";

const focusedComponentSelectListHeight = 640;

interface FocusedFindingToolbarState {
    component: SbomComponent;
    finding?: SbomComponentFinding;
    findings?: SbomComponentFinding[];
    findingsPage?: ComponentFindingsPage;
    components: SbomComponent[];
    findingPosition: number;
}

interface ComponentAnalysisState {
    label: string;
    status: "done" | "partial" | "todo";
    title: string;
}

interface ProjectVersionTitle {
    projectName?: string;
    versionName?: string;
    text: string;
}

interface FocusedFindingModalProps {
    analysis: FocusedFindingAnalysisController;
    sbomProject?: SingleResult<SbomProject>;
    projectVersion?: SbomProjectVersion;
    attributeDefinitions?: AttributeDefinition[];
    showAiAnalytics: boolean;
    canOpenFinding: boolean;
    onShowUsagesClick: (component: SbomComponent) => void;
    onFindingStatusSave: (finding: SbomComponentFinding) => void;
    onShowAiAnalyticsChange: (showAiAnalytics: boolean) => void;
}

function FocusedFindingModal(props: FocusedFindingModalProps) {
    const {analysis} = props;
    const component = analysis.state?.component;

    if (component === undefined) {
        return null;
    }

    const findingsPage = analysis.componentsFindings[component.id];
    const findings = findingsPage?.data;
    const finding = findings?.find(value => value.id === analysis.state?.findingId)
        ?? (analysis.state?.findingId === undefined ? findings?.[0] : undefined);
    const findingIndex = findings?.findIndex(value => value.id === finding?.id) ?? -1;
    const findingPosition = (findingsPage !== undefined && findingIndex >= 0) ? findingsPage.skip + findingIndex : -1;
    const projectVersionTitle = formatProjectVersionTitle();
    const modalTitle = formatModalTitle(projectVersionTitle);

    return <Modal
        open={true}
        title={<div className={styles['finding-focus-modal-title']}>
            <img className={styles['finding-focus-modal-logo']} src={"/img/logo-compact.svg"} alt={"SEAL"}/>
            <span className={styles['finding-focus-modal-title-text']} title={modalTitle}>
                Vulnerability analysis
                {renderProjectVersionTitle(projectVersionTitle)}
            </span>
            {renderFocusedContentSettings()}
        </div>}
        footer={null}
        width={"100vw"}
        styles={{
            body: {flex: 1, minHeight: 0, overflow: "hidden", padding: 0}
        }}
        wrapClassName={styles['finding-focus-modal']}
        onCancel={analysis.close}
    >
        <div className={styles['finding-focus-shell']}>
            {renderFocusedFindingToolbar({
                component,
                finding,
                findings,
                findingsPage,
                components: analysis.navigationComponents,
                findingPosition
            })}
            {renderFocusedFindingBody(finding, findings)}
        </div>
    </Modal>;

    function renderFocusedFindingToolbar(state: FocusedFindingToolbarState) {
        return <div className={styles['finding-focus-toolbar']}>
            <div className={styles['finding-focus-main']}>
                {renderFocusedComponentRow(state)}
                {renderFocusedFindingRow(state)}
            </div>
        </div>;
    }

    function renderFocusedComponentRow({component, components}: FocusedFindingToolbarState) {
        const componentIndex = components.findIndex(value => value.id === component.id);
        const {current, total} = getFocusedNavigationDisplayCount(componentIndex, components);

        return <div className={styles['finding-focus-nav-row']}>
            <span className={styles['finding-focus-row-label']}>Component</span>
            <div className={styles['finding-focus-row-actions']}>
                <Button icon={<DoubleLeftOutlined/>}
                        size={"small"}
                        title={"Previous component"}
                        disabled={!analysis.canMoveComponentPrev(componentIndex)}
                        onClick={() => analysis.moveComponent(-1)}/>
                {components.length > 0 && componentIndex >= 0 && renderFocusNavigationCount(current, total)}
                <Button icon={<DoubleRightOutlined/>}
                        size={"small"}
                        title={"Next component"}
                        disabled={!analysis.canMoveComponentNext(componentIndex, components)}
                        onClick={() => analysis.moveComponent(1)}/>
            </div>
            <div className={styles['finding-focus-row-content']}>
                <Select
                    className={styles['finding-focus-component-select']}
                    classNames={{popup: {listItem: styles['finding-focus-component-select-list-item']}}}
                    styles={{popup: {list: {maxHeight: focusedComponentSelectListHeight}}}}
                    size={"small"}
                    variant={"borderless"}
                    value={component.id}
                    open={analysis.componentSelectOpen}
                    popupMatchSelectWidth={false}
                    listHeight={focusedComponentSelectListHeight}
                    virtual={false}
                    showSearch={true}
                    loading={analysis.componentOptionsLoading}
                    filterOption={false}
                    popupRender={renderFocusedComponentSelectPopup}
                    notFoundContent={analysis.componentOptionsLoading ? renderFocusedComponentSelectLoading() : undefined}
                    optionRender={option => renderComponentSelectOption(option.data.component)}
                    onOpenChange={analysis.setComponentSelectOpen}
                    onSearch={analysis.searchComponentOptions}
                    onPopupScroll={analysis.onComponentOptionsPopupScroll}
                    onChange={analysis.selectComponent}
                    options={getFocusedComponentSelectComponents(components).map(optionComponent => ({
                        value: optionComponent.id,
                        label: formatComponentSelectLabel(optionComponent),
                        component: optionComponent
                    }))}
                />
                <div className={styles['finding-focus-summary']}>
                    <span className={styles['finding-focus-summary-group']}>
                        <span className={styles['finding-focus-summary-label']}>Findings</span>
                        <span className={styles['finding-focus-component-summary-value']}>
                            <FindingStatisticsImpact findingStatistics={component.findingStatistics} component={component} compact={true}/>
                        </span>
                    </span>
                    {component.analysisStatistics && <span className={`${styles['finding-focus-summary-group']} ${styles['finding-focus-summary-group-separated']}`}>
                        <span className={styles['finding-focus-summary-label']}>Analysis</span>
                        <FindingAnalysisStatistics
                            analysisStatistics={component.analysisStatistics}
                            style={AnalysisStyle.PROGRESS}
                            className={styles['finding-focus-component-analysis-progress']}
                        />
                    </span>}
                    <span className={`${styles['finding-focus-summary-group']} ${styles['finding-focus-summary-group-separated']}`}>
                        <span className={styles['finding-focus-summary-label']}>Graph</span>
                        <Button size={"small"}
                                icon={<ApartmentOutlined/>}
                                title={"Show component graph"}
                                onClick={() => props.onShowUsagesClick(component)}/>
                    </span>
                    {renderFocusedComponentLatestVersion(component)}
                </div>
            </div>
        </div>;
    }

    function renderFocusedComponentLatestVersion(component: SbomComponent) {
        if (!component.latestVersion || component.latestVersion === component.version) {
            return null;
        }

        const latestOsiUrl = createLatestVersionOsiUrl(component);

        return <span className={`${styles['finding-focus-summary-group']} ${styles['finding-focus-summary-group-separated']}`}>
            <span className={styles['finding-focus-summary-label']}>Latest</span>
            <span className={`${styles['finding-focus-latest-version']} ${styles['component-update-available']}`} title={"Update is available."}>
                <ArrowUpOutlined/>
                {latestOsiUrl
                    ? <Link to={latestOsiUrl} target={"_blank"} onClick={event => event.stopPropagation()}>{component.latestVersion}</Link>
                    : component.latestVersion}
            </span>
        </span>;
    }

    function createLatestVersionOsiUrl(component: SbomComponent) {
        if (!component.purl || !component.latestVersion) {
            return undefined;
        }

        try {
            const purl = PackageURL.fromString(component.purl);

            purl.version = component.latestVersion;

            return packageUrlToOsiUrl(purl);
        } catch {
            return undefined;
        }
    }

    function renderFocusedFindingRow({component, finding, findings, findingsPage, findingPosition}: FocusedFindingToolbarState) {
        const vulSourceItemId = finding?.vulSourceItem?.id;

        return <div className={styles['finding-focus-nav-row']}>
            <span className={styles['finding-focus-row-label']}>Finding</span>
            <div className={styles['finding-focus-row-actions']}>
                <Button icon={<LeftOutlined/>}
                        size={"small"}
                        title={"Previous finding"}
                        disabled={findingsPage === undefined || findingPosition <= 0}
                        onClick={() => analysis.moveFinding(-1)}/>
                {renderFocusedFindingNavigationCount(findingsPage, findingPosition)}
                <Button icon={<RightOutlined/>}
                        size={"small"}
                        title={"Next finding"}
                        disabled={findingsPage === undefined || findingPosition < 0 || findingPosition >= findingsPage.total - 1}
                        onClick={() => analysis.moveFinding(1)}/>
            </div>
            <div className={styles['finding-focus-row-content']}>
                <FocusedFindingSelect
                    componentId={component.id}
                    componentFindingsPageSize={analysis.componentFindingsPageSize}
                    findingOptionPageSize={analysis.findingOptionPageSize}
                    finding={finding}
                    findings={findings}
                    findingsPage={findingsPage}
                    findingPosition={findingPosition}
                    reloadComponentFindings={analysis.reloadComponentFindings}
                    onSelectFinding={analysis.selectFinding}
                />
                {finding && <div className={styles['finding-focus-summary']}>
                    {finding.severity && <span className={styles['finding-focus-summary-group']}>
                        <span className={styles['finding-focus-summary-label']}>Severity</span>
                        <Tag variant={"filled"}
                             className={`${styles['finding-focus-severity-tag']} ${sealScoreStyles['severity-' + finding.severity]}`}>
                            {finding.severity}
                        </Tag>
                    </span>}
                    {finding.vulSourceItem?.sourcePublished && <span className={`${styles['finding-focus-summary-group']} ${styles['finding-focus-summary-group-separated']}`}>
                        <span className={styles['finding-focus-summary-label']}>Published</span>
                        <span className={styles['finding-focus-finding-date']}>{formatDateTime(finding.vulSourceItem.sourcePublished)}</span>
                    </span>}
                    {props.canOpenFinding && vulSourceItemId && <Link to={`/vul-source-items/${vulSourceItemId}`}
                                                                      target={"_blank"}
                                                                      rel={"noopener noreferrer"}
                                                                      title={"Open vulnerability item"}
                                                                      aria-label={"Open vulnerability item"}
                                                                      className={`${styles['finding-focus-summary-group']} ${styles['finding-focus-summary-group-separated']}`}>
                        <ExportOutlined/>
                    </Link>}
                </div>}
            </div>
        </div>;
    }

    function renderFocusedFindingBody(finding?: SbomComponentFinding, findings?: SbomComponentFinding[]) {
        if (findings === undefined) {
            return <div className={styles['finding-focus-loading']}><Spin/></div>;
        }

        if (finding === undefined && analysis.state?.findingId !== undefined) {
            return <div className={styles['finding-focus-loading']}><Spin/></div>;
        }

        if (finding === undefined) {
            return <i>No vulnerability findings available for this component.</i>;
        }

        return <div className={styles['finding-focus-content']}>
            <VulSourceItemPreview
                item={finding.vulSourceItem}
                showTitle={false}
                attributeDefinitions={props.attributeDefinitions}
                showAiAnalytics={props.showAiAnalytics}
                framed={true}
            />

            <FindingStatusPanel sbomProject={props.sbomProject} finding={finding} onFindingStatusSave={props.onFindingStatusSave}/>
        </div>;
    }

    function renderFocusedContentSettings() {
        return <Popover
            trigger={"click"}
            placement={"bottomRight"}
            destroyOnHidden={true}
            content={<div className={styles['finding-focus-content-settings']}>
                <div className={styles['finding-focus-content-settings-title']}>Content</div>
                <Checkbox
                    checked={props.showAiAnalytics}
                    onChange={event => props.onShowAiAnalyticsChange(event.target.checked)}>
                    AI analytics
                </Checkbox>
            </div>}>
            <Button icon={<SettingOutlined/>} className={`${styles['finding-focus-title-button']} btn-seamless`} title={"Content settings"}/>
        </Popover>;
    }

    function renderProjectVersionTitle(projectVersionTitle?: ProjectVersionTitle) {
        if (projectVersionTitle === undefined) {
            return null;
        }

        return <>
            <span className={styles['finding-focus-modal-title-separator']} aria-hidden={true}/>
            {projectVersionTitle.projectName && <span className={styles['finding-focus-modal-title-context']}>{projectVersionTitle.projectName}</span>}
            {projectVersionTitle.projectName && projectVersionTitle.versionName && <span className={styles['finding-focus-modal-title-context-divider']}>·</span>}
            {projectVersionTitle.versionName &&
                <span className={styles['finding-focus-modal-title-context']}>{projectVersionTitle.versionName}</span>
            }
        </>;
    }

    function formatModalTitle(projectVersionTitle?: ProjectVersionTitle) {
        return projectVersionTitle === undefined
            ? "Vulnerability analysis"
            : `Vulnerability analysis ${projectVersionTitle.text}`;
    }

    function formatProjectVersionTitle(): ProjectVersionTitle | undefined {
        const projectName = props.sbomProject?.data?.name ?? props.projectVersion?.project?.name;
        const versionName = props.projectVersion?.name;

        if (projectName === undefined && versionName === undefined) {
            return undefined;
        }

        if (projectName !== undefined && versionName !== undefined) {
            return {projectName, versionName, text: `${projectName} · ${versionName}`};
        }

        return projectName !== undefined
            ? {projectName, text: projectName}
            : {versionName, text: versionName};
    }

    function getFocusedNavigationDisplayCount(componentIndex: number, components: SbomComponent[]) {
        if (componentIndex < 0) {
            return {current: 0, total: 0};
        }

        if (analysis.navigationComponentsPage === undefined) {
            return {current: componentIndex + 1, total: components.length};
        }

        return {
            current: analysis.navigationComponentsPage.skip + componentIndex + 1,
            total: analysis.navigationComponentsPage.total,
        };
    }

    function renderFocusNavigationCount(current: number, total: number) {
        return <span className={styles['finding-focus-nav-count']}>{current} of {total}</span>;
    }

    function renderFocusedFindingNavigationCount(findingsPage: ComponentFindingsPage | undefined, findingPosition: number) {
        if (findingsPage === undefined || findingPosition < 0) {
            return <span className={styles['finding-focus-nav-count']} aria-hidden={true}/>;
        }

        return renderFocusNavigationCount(findingPosition + 1, findingsPage.total);
    }

    function formatComponentSelectLabel(component: SbomComponent) {
        return [component.group, component.name, component.version].filter(Boolean).join(":");
    }

    function getFocusedComponentSelectComponents(components: SbomComponent[]) {
        const options = analysis.componentOptionsPage?.data.map(option => option.component) ?? components;

        if (component === undefined || options.some(option => option.id === component.id)) {
            return options;
        }

        if (!hasVulnerabilityFindings(component)) {
            return options;
        }

        return [component, ...options];
    }

    function renderFocusedComponentSelectPopup(menu: React.ReactNode) {
        const hasOptions = (analysis.componentOptionsPage?.data.length ?? 0) > 0;

        return <div>
            {menu}
            {analysis.componentOptionsLoading && hasOptions && renderFocusedComponentSelectLoading()}
        </div>;
    }

    function renderFocusedComponentSelectLoading() {
        return <div className={styles['finding-focus-component-select-loading']}>
            <Spin size={"small"}/>
            <span>Loading components...</span>
        </div>;
    }

    function renderComponentSelectOption(component?: SbomComponent) {
        if (component === undefined) {
            return null;
        }

        const analysisState = getComponentAnalysisState(component.analysisStatistics);
        const hasAnalysisImpact = !_.isEqual(component.findingStatistics, component.analysedFindingStatistics);
        const label = formatComponentSelectLabel(component);

        return <div className={styles['finding-focus-component-option']}>
            <div className={styles['finding-focus-component-option-name']}>
                <span title={label}>{label}</span>
            </div>
            <div className={styles['finding-focus-component-option-findings-before']}>
                <FindingStatistics compact={true} findingStatistics={component.findingStatistics}/>
            </div>
            <ArrowRightOutlined className={[
                styles['finding-focus-component-option-arrow'],
                !hasAnalysisImpact && styles['finding-focus-component-option-arrow-empty']
            ].filter(Boolean).join(" ")}/>
            <div className={styles['finding-focus-component-option-findings-after']}>
                {hasAnalysisImpact && <FindingStatistics compact={true} findingStatistics={component.analysedFindingStatistics}/>}
            </div>
            {analysisState && <Tag bordered={true}
                                   title={analysisState.title}
                                   className={styles[`finding-focus-component-analysis-${analysisState.status}`]}>
                {analysisState.label}
            </Tag>}
        </div>;
    }

    function getComponentAnalysisState(analysisStatistics?: any): ComponentAnalysisState | undefined {
        if (analysisStatistics == null) {
            return undefined;
        }

        const {percent} = calculateFindingAnalysisProgress(analysisStatistics);

        if (percent === 100) {
            return {label: "100%", status: "done", title: "All findings are analysed"};
        }

        if (percent === 0) {
            return {label: "0%", status: "todo", title: "No findings are analysed"};
        }

        return {label: `${percent}%`, status: "partial", title: `${percent}% of findings are analysed`};
    }

    function hasVulnerabilityFindings(component: SbomComponent) {
        return Object.keys(component.findingStatistics ?? {}).length > 0;
    }
}

export default FocusedFindingModal;
